import { useEffect, useRef, useState } from 'react';

const THRESHOLD = 64;   // px — qancha tortganda refresh ishlashi
const RESISTANCE = 2.5; // tortish qarshiligi

interface Options {
  onRefresh: () => Promise<void>;
  disabled?: boolean;
}

export function usePullToRefresh<T extends HTMLElement>({ onRefresh, disabled }: Options) {
  const containerRef = useRef<T>(null);
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const startY = useRef(0);
  const isPulling = useRef(false);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || disabled) return;

    const onTouchStart = (e: TouchEvent) => {
      if (el.scrollTop > 0 || isRefreshing) return;
      startY.current = e.touches[0].clientY;
      isPulling.current = true;
    };

    const onTouchMove = (e: TouchEvent) => {
      if (!isPulling.current || isRefreshing) return;
      const delta = (e.touches[0].clientY - startY.current) / RESISTANCE;
      if (delta <= 0) {
        setPullDistance(0);
        return;
      }
      e.preventDefault();
      setPullDistance(Math.min(delta, THRESHOLD * 1.5));
    };

    const onTouchEnd = async () => {
      if (!isPulling.current) return;
      isPulling.current = false;

      if (pullDistance >= THRESHOLD) {
        setIsRefreshing(true);
        setPullDistance(0);
        try {
          await onRefresh();
        } finally {
          setIsRefreshing(false);
        }
      } else {
        setPullDistance(0);
      }
    };

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', onTouchEnd);

    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
    };
  }, [disabled, isRefreshing, onRefresh, pullDistance]);

  const progress = Math.min(pullDistance / THRESHOLD, 1);

  return { containerRef, pullDistance, isRefreshing, progress };
}
